import { useEffect, useLayoutEffect, useState, useRef, useCallback } from "react";

// px from the bottom that still counts as "reached the bottom". Small enough
// that paging feels "at the bottom", large enough to absorb fractional
// scrollTop/scrollHeight rounding under Windows DPI scaling (sub-pixel noise).
const BOTTOM_THRESHOLD = 40;
// px slack when deciding "does the scroll container actually have a scrollbar"
const SCROLLABLE_EPS = 2;

function useInfiniteScrollWithoutCount(
	fetchData,
	page,
	scrollEvent = window,
	uniqueId,
	extra = {}
) {
	const [loading, setLoading] = useState(false);

	const pageRef = useRef(page);
	const prevPageRef = useRef(0);
	const hasMoreRef = useRef(true);
	const loadingRef = useRef(loading);
	const extraInfo = useRef(extra);

	useLayoutEffect(() => {
		pageRef.current = page;
		extraInfo.current = extra;
	}, [page, extra]);

	useEffect(() => {
		loadingRef.current = loading;
	}, [loading]);

	useEffect(() => {
		prevPageRef.current = page === 1 ? 0 : prevPageRef.current;
	}, [page]);

	const fetchMoreData = useCallback(async () => {
		if (loadingRef.current || !hasMoreRef.current) return;
		if (prevPageRef.current !== pageRef.current) {
			prevPageRef.current = pageRef.current;
			setLoading(true);
			const response = await fetchData(pageRef.current, extraInfo.current);

			if (!response || response.length === 0) {
				setLoading(false);
				hasMoreRef.current = false;
				return;
			}

			setLoading(false);
		}
	}, [fetchData]);

	const fetchMoreDataRef = useRef(fetchMoreData);
	useEffect(() => {
		fetchMoreDataRef.current = fetchMoreData;
	}, [fetchMoreData]);

	const getScrollContainer = useCallback(() => {
		if (uniqueId) {
			const el = document.getElementById(uniqueId);
			if (el) return el;
		}
		if (scrollEvent && scrollEvent !== window) return scrollEvent;
		return document.documentElement;
	}, [scrollEvent, uniqueId]);

	const handleScroll = useCallback(
		async (e) => {
			if (loadingRef.current) return;

			const doc = uniqueId ? document.getElementById(uniqueId) : null;
			if (uniqueId && doc) {
				if (
					doc.scrollHeight - (doc.scrollTop + doc.clientHeight) <=
					BOTTOM_THRESHOLD
				) {
					await fetchMoreData();
				}
			} else if (
				document.documentElement.scrollHeight -
					(window.innerHeight + document.documentElement.scrollTop) <=
				BOTTOM_THRESHOLD
			) {
				await fetchMoreData();
			}
		},
		[fetchMoreData, uniqueId]
	);

	useEffect(() => {
		scrollEvent.addEventListener("scroll", handleScroll);
		return () => scrollEvent.removeEventListener("scroll", handleScroll);
	}, [scrollEvent, handleScroll]);

	// Viewport-fill fallback: after each page, if there's no scrollbar the
	// user can't scroll to request more, so fetch the next page until the
	// container overflows or the data is exhausted (response came back empty,
	// handled by hasMoreRef in fetchMoreData).
	useEffect(() => {
		if (loadingRef.current || !hasMoreRef.current) return;

		const raf =
			window.requestAnimationFrame || ((cb) => window.setTimeout(cb, 0));
		const caf = window.cancelAnimationFrame || window.clearTimeout;

		const id = raf(() => {
			if (loadingRef.current || !hasMoreRef.current) return;
			const el = getScrollContainer();
			if (!el || el.clientHeight <= 0) return;
			const scrollable =
				el.scrollHeight > el.clientHeight + SCROLLABLE_EPS;
			if (!scrollable) {
				fetchMoreDataRef.current();
			}
		});
		return () => caf(id);
	}, [page, loading, getScrollContainer]);

	const gotoTop = useCallback(() => {
		scrollEvent.scroll({ top: 0, left: 0, behavior: "smooth" });
	}, [scrollEvent]);

	return { loading, gotoTop, hasMoreRef };
}

export default useInfiniteScrollWithoutCount;
